import React, { PropsWithChildren, ReactNode, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { isHotelPackage } from 'lib/offer/offerTypes'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import TextLink from 'LuxKit/TextLink'
import { useIntl } from 'react-intl'

export const StyledAccommodationRoomTileDetails = styled(Group)`
  padding: ${rem(20)} ${rem(20)} 0;
`

interface RoomPackageDetails {
  name: string
  description?: string
}

function extractHotelRoomPackageDetails(roomPackage: App.HotelPackage): RoomPackageDetails {
  return {
    name: roomPackage.roomType.name,
    description: roomPackage.roomType.description,
  }
}

// Match a line that starts with two asterisks, as that implies we're dealing with Markdown. For
// example: "\n**Size:** 165m²\n\n**Sleeps:** 4 guests\n\n**Partial sea view**\n".
const BEDBANK_RICH_DESC_REGEX = /^\*\*/m

function extractBedbankRoomPackageDetails(roomPackage: App.BedbankPackage): RoomPackageDetails {
  return {
    name: roomPackage.name,
    description: !BEDBANK_RICH_DESC_REGEX.test(roomPackage.description) ? roomPackage.description : undefined,
  }
}

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage
  urgencyLabels?: ReactNode
  actions?: ReactNode
  onViewRoomDetails?: () => void
}

function AccommodationRoomTileDetailsVariant(props: PropsWithChildren<Props>) {
  const {
    actions,
    roomPackage,
    urgencyLabels,
    onViewRoomDetails,
  } = props

  const intl = useIntl()
  const details = useMemo(() => {
    if (isHotelPackage(roomPackage)) {
      return extractHotelRoomPackageDetails(roomPackage)
    } else { // is App.BedbankPackage
      return extractBedbankRoomPackageDetails(roomPackage)
    }
  }, [roomPackage])

  return <StyledAccommodationRoomTileDetails direction="vertical" gap={12}>
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
        <Heading variant="heading4">{details.name}</Heading>
        {actions && <Group direction="horizontal" gap={4} horizontalAlign="end" wrap="wrap" noDisplayWhenEmpty>
          {actions}
        </Group>}
      </Group>
      {onViewRoomDetails && <TextLink size="medium" onClick={onViewRoomDetails}>
        {intl.formatMessage({
          id: 'hl6aZwH/aJ',
          defaultMessage: 'View room details',
          description: 'Link text to view room details',
        })}
      </TextLink>}
      {urgencyLabels && <LabelGroup>{urgencyLabels}</LabelGroup>}
    </VerticalSpacer>
    <AccommodationRoomDetailsList roomPackage={roomPackage} />
  </StyledAccommodationRoomTileDetails>
}

export default AccommodationRoomTileDetailsVariant
